import { TooltipDesktop } from '@cian/ui-kit';
import { Dispatch } from '@reduxjs/toolkit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackAddOfferToComparison, trackDeleteOfferFromComparison } from './tracking';
import { requestAuthentication } from '../../actions/authentication';
import { addOfferToComparison, deleteOfferFromComparison } from '../../actions/comparison';
import { ComparisonControl, ComparisonControlNew } from '../../components/ComparisonControl';
import { ComparisonControlTooltip } from '../../components/ComparisonControlTooltip';
import { selectIsAuthenticated } from '../../selectors';
import { getOffer } from '../../selectors/newObject';
import { getAgent } from '../../selectors/newObject/getAgent';
import { getOffersComparisonStatus } from '../../selectors/offersComparison';
import { getOffersComparison } from '../../selectors/offersComparison/getOffersComparison';
import { IThunkActionCreator } from '../../store';
import { EStatus } from '../../types/offersComparisonStatus';
import { getCustomUrl } from '../../utils/tracking/getCustomUrl';

interface IComparisonControlContainerProps {
  isNewButton?: boolean;
}

export function ComparisonControlContainer({ isNewButton }: IComparisonControlContainerProps) {
  const dispatch = useDispatch();
  const comparisonStatus = useSelector(getOffersComparisonStatus);
  const offer = useSelector(getOffer);
  const agent = useSelector(getAgent);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const comparison = useSelector(getOffersComparison);

  const isAdded = React.useMemo(() => {
    return (comparisonStatus && comparisonStatus.status) === EStatus.Added;
  }, [comparisonStatus]);

  const isUnavailable = React.useMemo(
    () => comparisonStatus?.status === EStatus.Unavailable,

    [comparisonStatus],
  );

  const Control = isNewButton ? ComparisonControlNew : ComparisonControl;

  const isComparisonDescriptionAvailable = React.useMemo(() => {
    return comparisonStatus?.description?.length;
  }, [comparisonStatus]);

  const handleClick = React.useCallback(() => {
    const offerId = offer.id;
    if (isAdded) {
      dispatch(deleteOfferFromComparison({ offerId }));
      trackDeleteOfferFromComparison({ label: getCustomUrl(offer, agent) });
    } else {
      if (!isAuthenticated && !comparison?.offers.length) {
        dispatch(
          requestAuthentication({
            source: 'сompareOffers',
            viewData: { headingType: 'compareOffers' },
            onSuccess: (): IThunkActionCreator => (dispatch: Dispatch) => {
              dispatch(addOfferToComparison({ offerId }));
            },
          }),
        );
      } else {
        dispatch(addOfferToComparison({ offerId }));
      }
      trackAddOfferToComparison({ label: getCustomUrl(offer, agent) });
    }
  }, [agent, comparison?.offers.length, dispatch, isAdded, isAuthenticated, offer]);

  if (!comparisonStatus) {
    return null;
  }

  return !isComparisonDescriptionAvailable ? (
    <Control onClick={handleClick} added={isAdded} disabled={isUnavailable} />
  ) : (
    <TooltipDesktop
      theme="white"
      placement="bottom"
      arrow={true}
      title={<ComparisonControlTooltip text={comparisonStatus.description} />}
    >
      <div>
        <Control onClick={handleClick} added={isAdded} disabled={isUnavailable} />
      </div>
    </TooltipDesktop>
  );
}
